import { Immutable, produce } from "immer";
import { useAtom } from "jotai";
import { useCallback, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import invariant from "tiny-invariant";

import { Button } from "@/components/ui/button";
import {
    CmsBlock,
    CmsContainerBlock,
    CmsFeaturedStreamingChannelBlock,
    CmsRowBlock,
    CmsTeaserBlock,
    CmsTournamentHighlightButton,
    CmsTournamentPodiumBlock,
    LatestContentBlock,
} from "@/gen/realtime";

import { LayoutEditDispatch, LayoutEditState } from "../layoutEditState";
import { LayoutMetadata } from "../layoutMetadata";
import { useUndoHandler } from "../undoHandler";
import { ColumnForm } from "./ColumnForm";
import { ContainerForm } from "./ContainerForm";
import { FeatureFlagModal } from "./FeatureFlagModal";
import { FeaturedStreamingChannelForm } from "./FeaturedStreamingChannelForm";
import { GuessTheMoveLeaderboardForm } from "./GuessTheMoveLeaderboardForm";
import { LatestContentForm } from "./LatestContentForm";
import { MetadataForm } from "./MetadataForm";
import { RowForm } from "./RowForm";
import { TeaserForm } from "./TeaserForm";
import { TournamentHighlightButtonForm } from "./TournamentHighlightButtonForm";
import { TournamentPodiumForm } from "@/components/forms/TournamentPodiumForm";

export const LayoutEditSidebar = ({
    state,
    dispatch,
    metadataForm,
    onSubmit,
    customMetadataComponent,
}: {
    state: Immutable<LayoutEditState>;
    dispatch: LayoutEditDispatch;
    metadataForm?: UseFormReturn<LayoutMetadata>;
    onSubmit: () => void;
    customMetadataComponent?: React.ReactNode;
}) => {
    const activeBlock = state.activeBlock;
    const [featureFlagModalOpen, setFeatureFlagModalOpen] = useState(false);

    return (
        <div className="m-2 flex flex-grow basis-1 flex-col gap-4">
            <div className="sticky top-0">
                {activeBlock ? (
                    <div className="max-h-[100vh] space-y-4 overflow-auto py-4">
                        <div className="flex gap-4">
                            <div className="flex flex-1 gap-2">
                                <Button
                                    onClick={() => {
                                        dispatch({ type: "duplicate-block", blockId: activeBlock });
                                    }}
                                    variant="outline"
                                >
                                    Duplicate block
                                </Button>
                                <Button
                                    onClick={() => {
                                        dispatch({
                                            type: "delete-block",
                                            blockId: activeBlock,
                                        });
                                    }}
                                    variant="destructive"
                                >
                                    Delete block
                                </Button>
                            </div>

                            <FeatureFlagButton
                                blockId={activeBlock}
                                state={state}
                                onClick={() => setFeatureFlagModalOpen(true)}
                            />
                        </div>
                        <BlockEdit blockId={activeBlock} state={state} key={activeBlock} />
                        <FeatureFlagModalWrapper
                            blockId={activeBlock}
                            state={state}
                            open={featureFlagModalOpen}
                            onSave={onSubmit}
                            onOpenChange={setFeatureFlagModalOpen}
                        />
                    </div>
                ) : (
                    customMetadataComponent ||
                    (metadataForm && <MetadataForm form={metadataForm} onSubmit={onSubmit} />)
                )}
            </div>
        </div>
    );
};

export const BlockEdit = ({
    blockId,
    state,
}: {
    blockId: string;
    state: Immutable<LayoutEditState>;
}) => {
    const [block, setBlock] = useAtom(state.layout.blocks[blockId]);
    const { onPatches } = useUndoHandler();

    const mutateBlock = useCallback(
        (mutate: (block: CmsBlock) => void) => {
            setBlock(prev =>
                produce(
                    prev,
                    draft => {
                        mutate(draft);
                    },
                    onPatches(blockId)
                )
            );
        },
        [blockId, onPatches, setBlock]
    );

    switch (block.kind.t) {
        case "Teaser":
            return (
                <TeaserForm
                    block={block.kind.c}
                    setBlock={(mutate: (block: CmsTeaserBlock) => void) => {
                        mutateBlock(draft => {
                            invariant(draft.kind.t === "Teaser");
                            mutate(draft.kind.c);
                        });
                    }}
                />
            );
        case "Container":
            return (
                <ContainerForm
                    block={block.kind.c}
                    setBlock={(mutate: (block: CmsContainerBlock) => void) => {
                        mutateBlock(draft => {
                            invariant(draft.kind.t === "Container");
                            mutate(draft.kind.c);
                        });
                    }}
                />
            );
        case "Row":
            return (
                <RowForm
                    block={block.kind.c}
                    setBlock={(mutate: (block: CmsRowBlock) => void) => {
                        mutateBlock(draft => {
                            invariant(draft.kind.t === "Row");
                            mutate(draft.kind.c);
                        });
                    }}
                />
            );
        case "LatestContent":
            return (
                <LatestContentForm
                    block={block.kind.c}
                    setBlock={(mutate: (block: LatestContentBlock) => void) => {
                        mutateBlock(draft => {
                            invariant(draft.kind.t === "LatestContent");
                            mutate(draft.kind.c);
                        });
                    }}
                />
            );
        case "Column":
            return <ColumnForm block={block.kind.c} />;
        case "TournamentHighlightButton":
            return (
                <TournamentHighlightButtonForm
                    block={block.kind.c}
                    setBlock={(mutate: (block: CmsTournamentHighlightButton) => void) => {
                        mutateBlock(draft => {
                            invariant(draft.kind.t === "TournamentHighlightButton");
                            mutate(draft.kind.c);
                        });
                    }}
                />
            );
        case "GuessTheMoveLeaderboard":
            return <GuessTheMoveLeaderboardForm />;
        case "FeaturedStreamingChannel":
            return (
                <FeaturedStreamingChannelForm
                    block={block.kind.c}
                    setBlock={(mutate: (block: CmsFeaturedStreamingChannelBlock) => void) => {
                        mutateBlock(draft => {
                            invariant(draft.kind.t === "FeaturedStreamingChannel");
                            mutate(draft.kind.c);
                        });
                    }}
                />
            );
        case "TournamentPodium":
            return (
                <TournamentPodiumForm
                    block={block.kind.c}
                    setBlock={(mutate: (block: CmsTournamentPodiumBlock) => void) => {
                        mutateBlock(draft => {
                            invariant(draft.kind.t === "TournamentPodium");
                            mutate(draft.kind.c);
                        });
                    }}
                />
            );
        default:
            return null;
    }
};

export const FeatureFlagButton = ({
    blockId,
    state,
    onClick,
}: {
    blockId: string;
    state: Immutable<LayoutEditState>;
    onClick: () => void;
}) => {
    const [block] = useAtom(state.layout.blocks[blockId]);

    return (
        <Button onClick={onClick} variant={block.featureFlagId ? "default" : "outline"}>
            Feature flag
            {block.featureFlagId && " ✓"}
        </Button>
    );
};

export const FeatureFlagModalWrapper = ({
    blockId,
    state,
    open,
    onOpenChange,
    onSave,
}: {
    blockId: string;
    state: Immutable<LayoutEditState>;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSave: () => void;
}) => {
    const [block, setBlock] = useAtom(state.layout.blocks[blockId]);
    const { onPatches } = useUndoHandler();

    const handleSave = useCallback(
        (featureFlagId: string | undefined) => {
            setBlock(prev =>
                produce(
                    prev,
                    draft => {
                        draft.featureFlagId = featureFlagId ?? null;
                    },
                    onPatches(blockId)
                )
            );
            onSave();
        },
        [blockId, onPatches, onSave, setBlock]
    );

    return (
        <FeatureFlagModal
            open={open}
            onOpenChange={onOpenChange}
            currentFeatureFlagId={block.featureFlagId ?? undefined}
            onSave={handleSave}
        />
    );
};
